import { Component, EventEmitter, Input, OnInit, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { ESRI } from '../../../esri-loader-util';
import { BooleanConverter, InputConverter } from '../../../inputConverter';
import { BaseComponent } from '../../base.component';

export interface ISketchTool {
    creationMode: CreationMode;
    containerElement: HTMLElement;
    defaultCreateOptions: NonNullable<__esri.SketchDefaultCreateOptions>;
    defaultUpdateOptions: NonNullable<__esri.SketchDefaultUpdateOptions>;
    deleteGraphicsWhenDeactivated: boolean;
    hideGraphicsLayerWhenDeactivated: boolean;
    graphicsLayer: NonNullable<__esri.GraphicsLayer>;
    graphics: NonNullable<__esri.Graphic>[];
    geometries: NonNullable<__esri.Geometry>[];
    mapView: NonNullable<__esri.MapView>;
    sketch: NonNullable<__esri.Sketch>;
    activate(toolType: string): any;
    load(toolType: string): Promise<void>;
    deactivate();
    destroy();
    oncomplete: EventEmitter<boolean>;
}

let Sketch: __esri.SketchConstructor;
let GraphicsLayer: __esri.GraphicsLayerConstructor;

export type CreationMode = 'update' | 'single' | 'continuous';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-sketch-tool-ng',
    styleUrls: ['../../sharedStyles/base.scss'],
    templateUrl: './sketch-tool.component.html'
})

export class SketchToolComponent extends BaseComponent implements OnInit {

    @Input() containerElement: HTMLElement;
    @Input() @InputConverter(BooleanConverter) deleteGraphicsWhenDeactivated: boolean = true;
    @Input() @InputConverter(BooleanConverter) hideGraphicsLayerWhenDeactivated: boolean = true;
    @Input() graphicsLayer: NonNullable<__esri.GraphicsLayer>;
    @Input() mapView: NonNullable<__esri.MapView>;
    @Input() sketch: NonNullable<__esri.Sketch>;

    @Output() readonly oncomplete = new EventEmitter<boolean>();

    constructor(_vcr: ViewContainerRef) {
        super(_vcr);
        this.registerEvent('oncomplete', this.oncomplete);
    }

    async ngOnInit() {
        [GraphicsLayer, Sketch] = await Promise.all([ESRI.GraphicsLayer, ESRI.Sketch]);
    }

    _creationMode: CreationMode = 'update';
    @Input()
    get creationMode() {
        return this._creationMode;
    }
    set creationMode(value: CreationMode) {
        this._creationMode = value;
        if (this.sketch) {
            this.sketch.creationMode = value;
        }
    }

    _defaultCreateOptions: __esri.SketchDefaultCreateOptions = { mode: 'click' };
    @Input()
    get defaultCreateOptions() {
        return this._defaultCreateOptions;
    }
    set defaultCreateOptions(value: any) {
        this._defaultCreateOptions = value;
        if (this.sketch) {
            this.sketch.defaultCreateOptions = value;
        }
    }

    _defaultUpdateOptions: __esri.SketchDefaultUpdateOptions = { tool: 'transform' };
    @Input()
    get defaultUpdateOptions() {
        return this._defaultUpdateOptions;
    }
    set defaultUpdateOptions(value: any) {
        this._defaultUpdateOptions = value;
        if (this.sketch) {
            this.sketch.defaultUpdateOptions = value;
        }
    }

    @Input()
    get graphics(): __esri.Graphic[] {
        if (this.graphicsLayer) {
            return this.graphicsLayer.graphics.toArray();
        }
        return null;
    }

    @Input()
    get geometries(): __esri.Geometry[] {
        if (this.graphics) {
            return this.graphics.map(x => x.geometry);
        }
        return null;
    }

    @Input()
    public activate = async (toolType: 'point' | 'polyline' | 'polygon' | 'rectangle' | 'circle', mode: 'hybrid' | 'freehand' | 'click' = null) => {
        if (!this.sketch || this.sketch.destroyed) {
            await this.load(toolType, mode);
        } else {
            this.activateTool(toolType, mode);
        }
    }

    @Input()
    public deactivate = async () => {
        if (this.sketch) {
            this.sketch.cancel();
        }
        if (this.graphicsLayer) {
            if (this.deleteGraphicsWhenDeactivated) {
                this.graphicsLayer.removeAll();
            }
            if (this.hideGraphicsLayerWhenDeactivated) {
                this.graphicsLayer.visible = false;
            }
        }
    }

    @Input()
    public destroy = async () => {
        this.deactivate();
        if (this.sketch && !this.sketch.destroyed) {
            this.sketch.destroy();
        }
        if (this.graphicsLayer) {
            this.graphicsLayer.removeAll();
            this.mapView.map.remove(this.graphicsLayer);
        }
    }

    async load(toolType: 'point' | 'polyline' | 'polygon' | 'rectangle' | 'circle', mode: 'hybrid' | 'freehand' | 'click' = null) {
        this.addGraphicsLayerToMapIfNotPresent();
        if (!this.containerElement) {
            this.containerElement = this.hostElement;
        }
        this.containerElement.innerHTML = '';
        const sketchContainer = document.createElement('div');
        this.containerElement.appendChild(sketchContainer);
        sketchContainer.style.display = 'none';
        this.graphicsLayer.when(async () => {
            this.sketch = new Sketch({
                container: sketchContainer,
                layer: this.graphicsLayer,
                view: this.mapView,
                creationMode: this._creationMode,
                defaultCreateOptions: this._defaultCreateOptions,
                defaultUpdateOptions: this._defaultUpdateOptions
            });

            await this.activateTool(toolType, mode);

            this.sketch.on('create', (event: any) => {
                this.processSketchEvent(event);
            });

            this.sketch.on('update', (event: any) => {
                this.processSketchEvent(event);
            });
        });
    }

    async activateTool(toolType: 'point' | 'polyline' | 'polygon' | 'rectangle' | 'circle', mode: 'hybrid' | 'freehand' | 'click' = null) {
        const esriModeDefaults = {
            point: null,
            polyline: 'hybrid',
            polygon: 'hybrid',
            rectangle: 'freehand',
            circle: 'freehand'
        };
        this.addGraphicsLayerToMapIfNotPresent();
        if (!this.sketch.layer) {
            this.sketch.layer = this.graphicsLayer;
        }
        this.sketch.create(toolType, { mode: mode ? mode : esriModeDefaults[toolType] });
    }

    addGraphicsLayerToMapIfNotPresent() {
        if (!this.graphicsLayer) {
            this.graphicsLayer = new GraphicsLayer(<any>{ title: 'Sketch Tool', hideInLegend: true });
        }
        if (!this.mapView.map.allLayers.some(x => x === this.graphicsLayer)) {
            this.mapView.map.add(this.graphicsLayer);
        }
        this.graphicsLayer.visible = true;
    }

    processSketchEvent(event: any) {
        if (event.state === 'complete') {
            if (event.graphic) {
                this.graphicsLayer.add(event.graphic);
            }
            this.oncomplete.emit(true);
        }
    }
}
